import React, { useMemo } from 'react';
import { Select, Space } from 'antd';

const { Option } = Select;

/**
 * BuilderConfigEditor - cascading model/resolution/duration selector
 *
 * Props:
 *   builderType: "IMAGE" | "VIDEO"
 *   videoModels: getModelListUnified models (flat list, no mode split)
 *   imageModels: getImageModelListUnified models (flat list, no mode split)
 *   value: { modelId, resolution, duration, aspectRatio }
 *   onChange: (config) => void
 */
const BuilderConfigEditor = ({ builderType, videoModels, imageModels, value, onChange }) => {
  const config = value || {};

  const models = useMemo(() => {
    if (builderType === 'VIDEO') {
      return videoModels || [];
    }
    if (builderType === 'IMAGE') {
      return imageModels || [];
    }
    return [];
  }, [builderType, videoModels, imageModels]);

  const selectedModel = useMemo(() => {
    return models.find((m) => m.id === config.modelId);
  }, [models, config.modelId]);

  // Extract unique resolutions from the selected model's aspect ratios
  const resolutions = useMemo(() => {
    if (!selectedModel?.aspect_ratios) return [];
    const resSet = new Map();
    selectedModel.aspect_ratios.forEach((ar) => {
      (ar.resolutions || []).forEach((r) => {
        const val = r.resolution?.api_enum_value;
        if (val && !resSet.has(val)) {
          resSet.set(val, r.resolution);
        }
      });
    });
    return Array.from(resSet.values());
  }, [selectedModel]);

  // Extract unique durations (video only) from selected model
  const durations = useMemo(() => {
    if (builderType !== 'VIDEO' || !selectedModel?.aspect_ratios) return [];
    const durSet = new Map();
    selectedModel.aspect_ratios.forEach((ar) => {
      (ar.resolutions || []).forEach((r) => {
        (r.durations || []).forEach((d) => {
          const val = d.duration?.api_enum_value;
          if (val && !durSet.has(val)) {
            durSet.set(val, d.duration);
          }
        });
      });
    });
    return Array.from(durSet.values());
  }, [builderType, selectedModel]);

  const updateConfig = (patch) => {
    onChange({ ...config, ...patch });
  };

  if (!builderType) return null;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {/* Model Select */}
      <Select
        placeholder="Select model"
        value={config.modelId || undefined}
        onChange={(val) => updateConfig({ modelId: val, resolution: undefined, duration: undefined })}
        style={{ width: '100%' }}
        allowClear
        showSearch
        optionFilterProp="children"
      >
        {models.map((m) => (
          <Option key={m.id} value={m.id}>
            {m.id}{m.is_default ? ' (default)' : ''}
          </Option>
        ))}
      </Select>

      {/* Resolution Select */}
      {resolutions.length > 0 && (
        <Select
          placeholder="Select resolution (optional)"
          value={config.resolution || undefined}
          onChange={(val) => updateConfig({ resolution: val })}
          style={{ width: '100%' }}
          allowClear
        >
          {resolutions.map((r) => (
            <Option key={r.api_enum_value} value={r.api_enum_value}>
              {r.name || r.api_enum_value}
            </Option>
          ))}
        </Select>
      )}

      {/* Duration Select (video only) */}
      {builderType === 'VIDEO' && durations.length > 0 && (
        <Select
          placeholder="Select duration (optional)"
          value={config.duration || undefined}
          onChange={(val) => updateConfig({ duration: val })}
          style={{ width: '100%' }}
          allowClear
        >
          {durations.map((d) => (
            <Option key={d.api_enum_value} value={d.api_enum_value}>
              {d.seconds ? `${d.seconds}s` : (d.name || d.api_enum_value)}
            </Option>
          ))}
        </Select>
      )}
    </Space>
  );
};

export default BuilderConfigEditor;
